
import React, { useState, useRef } from 'react';
import { User as UserType, Order, Language, ArtItem } from '../types';
import { User, Camera, Mail, Lock, Save, Box, Trash2, ShieldCheck, Loader2, CameraIcon, LogOut, ChevronRight, Hash, Calendar, CheckCircle2, Clock, XCircle, DollarSign } from 'lucide-react';
import UserOrders from './UserOrders';

interface UserProfileProps {
  user: UserType;
  orders: Order[];
  language: Language;
  arts?: ArtItem[];
  onUpdateUser: (updatedData: Partial<UserType>) => Promise<boolean>;
  onLogout: () => void;
}

const UserProfile: React.FC<UserProfileProps> = ({ user, orders, language, arts = [], onUpdateUser, onLogout }) => {
  const [activeTab, setActiveTab] = useState<'profile' | 'security' | 'orders'>('profile');
  const [isSaving, setIsSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form states
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const t = {
    pt: {
      title: 'Câmara do Perfil',
      sub: 'Gestão de Identidade e Acervo',
      personalTab: 'Dados Pessoais',
      securityTab: 'Segurança',
      ordersTab: 'Histórico',
      nameLabel: 'Apelido (Nickname)',
      emailLabel: 'E-mail de Contato',
      photoLabel: 'Foto de Perfil',
      saveBtn: 'Salvar Alterações',
      passLabel: 'Nova Senha',
      confirmPass: 'Confirmar Senha',
      logout: 'Sair da Conta',
      success: 'Identidade atualizada com sucesso!',
      error: 'Erro ao atualizar dados.',
      passMismatch: 'As senhas não coincidem.',
      placeholderName: 'Como o clã deve te chamar?',
      placeholderEmail: 'Seu e-mail sagrado...'
    },
    en: {
      title: 'Profile Chamber',
      sub: 'Identity and Collection Management',
      personalTab: 'Personal Data',
      securityTab: 'Security',
      ordersTab: 'History',
      nameLabel: 'Nickname',
      emailLabel: 'Contact Email',
      photoLabel: 'Profile Photo',
      saveBtn: 'Save Changes',
      passLabel: 'New Password',
      confirmPass: 'Confirm Password',
      logout: 'Logout',
      success: 'Identity successfully updated!',
      error: 'Error updating data.',
      passMismatch: 'Passwords do not match.',
      placeholderName: 'How should the clan call you?',
      placeholderEmail: 'Your sacred email...'
    }
  }[language];

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64 = reader.result as string;
        await onUpdateUser({ avatarUrl: base64 });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdateInfo = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setMsg(null);

    const updateData: Partial<UserType> = { name, email };
    
    if (activeTab === 'security' && newPassword) {
      if (newPassword !== confirmPassword) {
        setMsg({ type: 'error', text: t.passMismatch });
        setIsSaving(false);
        return;
      }
      updateData.password = newPassword;
    }

    const success = await onUpdateUser(updateData);
    if (success) {
      setMsg({ type: 'success', text: t.success });
      setNewPassword('');
      setConfirmPassword('');
    } else {
      setMsg({ type: 'error', text: t.error });
    }
    setIsSaving(false);
  };

  return (
    <div className="max-w-6xl mx-auto py-12 px-4 animate-in fade-in slide-in-from-bottom-8 duration-700">
      <div className="flex flex-col md:flex-row gap-12">
        
        {/* SIDEBAR */}
        <div className="w-full md:w-80 space-y-8">
          <div className="bg-zinc-900 border border-white/5 p-8 text-center relative overflow-hidden group">
            <div className="absolute top-0 left-0 w-full h-1 bg-[#c084fc] shadow-[0_0_10px_#c084fc]" />
            
            <div className="relative inline-block mb-6">
              <div className="w-32 h-32 rounded-full border-4 border-zinc-800 overflow-hidden bg-black group-hover:border-[#c084fc] transition-all">
                {user.avatarUrl ? (
                  <img src={user.avatarUrl} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-zinc-950">
                    <User className="w-12 h-12 text-zinc-800" />
                  </div>
                )}
              </div>
              <button 
                onClick={() => fileInputRef.current?.click()}
                className="absolute bottom-0 right-0 p-2.5 bg-white text-black hover:bg-[#c084fc] hover:text-white transition-all shadow-xl rounded-full border-2 border-zinc-900"
              >
                <CameraIcon className="w-4 h-4" />
              </button>
              <input ref={fileInputRef} type="file" className="hidden" accept="image/*" onChange={handlePhotoUpload} />
            </div>

            <h3 className="text-xl font-black text-white uppercase tracking-tighter mb-1 truncate">{user.name}</h3>
            <p className="text-[10px] text-zinc-500 font-black uppercase tracking-[0.3em] mb-6">{user.email}</p>
            
            <button 
              onClick={onLogout}
              className="w-full py-3 bg-zinc-950 border border-zinc-800 text-zinc-600 hover:text-red-500 hover:border-red-500/30 transition-all text-[9px] font-black uppercase tracking-widest flex items-center justify-center gap-2"
            >
              <LogOut className="w-3 h-3" /> {t.logout}
            </button>
          </div>

          <div className="bg-zinc-900 border border-white/5 p-2 space-y-1">
            {[
              { id: 'profile', label: t.personalTab, icon: User },
              { id: 'security', label: t.securityTab, icon: Lock },
              { id: 'orders', label: t.ordersTab, icon: Box }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`w-full flex items-center justify-between p-4 transition-all group ${
                  activeTab === tab.id ? 'bg-[#c084fc] text-white shadow-lg' : 'text-zinc-500 hover:bg-white/5'
                }`}
              >
                <div className="flex items-center gap-3">
                  <tab.icon className="w-4 h-4" />
                  <span className="text-[10px] font-black uppercase tracking-widest">{tab.label}</span>
                </div>
                <ChevronRight className={`w-3 h-3 transition-transform ${activeTab === tab.id ? 'translate-x-1' : 'opacity-0'}`} />
              </button>
            ))}
          </div>
        </div>

        {/* MAIN CONTENT */}
        <div className="flex-1 bg-zinc-900/40 border border-white/5 p-8 md:p-12 relative overflow-hidden backdrop-blur-xl">
          <div className="mb-12">
            <h2 className="text-3xl font-black text-white uppercase tracking-tighter oil-text">{activeTab === 'orders' ? t.ordersTab : t.title}</h2>
            <p className="text-zinc-600 text-[10px] font-black uppercase tracking-[0.4em]">{t.sub}</p>
          </div>

          {msg && (
            <div className={`p-4 mb-8 text-[10px] font-black uppercase tracking-widest border ${
              msg.type === 'success' ? 'bg-green-500/10 border-green-500 text-green-500' : 'bg-red-500/10 border-red-500 text-red-500'
            }`}>
              {msg.text}
            </div>
          )}

          {activeTab === 'orders' ? (
            <UserOrders orders={orders} language={language} arts={arts} />
          ) : (
            <form onSubmit={handleUpdateInfo} className="space-y-8 max-w-xl">
              {activeTab === 'profile' && (
                <>
                  <div className="space-y-3">
                    <label className="text-[9px] font-black text-zinc-500 uppercase tracking-[0.3em] flex items-center gap-2">
                      <User className="w-3.5 h-3.5" /> {t.nameLabel}
                    </label>
                    <input 
                      type="text" 
                      value={name} 
                      onChange={e => setName(e.target.value)} 
                      placeholder={t.placeholderName}
                      className="w-full bg-black border border-zinc-800 p-5 text-white outline-none focus:border-[#c084fc] transition-all text-sm font-bold" 
                    />
                  </div>
                  <div className="space-y-3">
                    <label className="text-[9px] font-black text-zinc-500 uppercase tracking-[0.3em] flex items-center gap-2">
                      <Mail className="w-3.5 h-3.5" /> {t.emailLabel}
                    </label>
                    <input 
                      type="email" 
                      value={email} 
                      onChange={e => setEmail(e.target.value)} 
                      placeholder={t.placeholderEmail}
                      className="w-full bg-black border border-zinc-800 p-5 text-white outline-none focus:border-[#c084fc] transition-all text-sm font-bold" 
                    />
                  </div>
                </>
              )}

              {activeTab === 'security' && (
                <>
                  <div className="space-y-3">
                    <label className="text-[9px] font-black text-zinc-500 uppercase tracking-[0.3em] flex items-center gap-2">
                      <Lock className="w-3.5 h-3.5" /> {t.passLabel}
                    </label>
                    <input 
                      type="password" 
                      value={newPassword} 
                      onChange={e => setNewPassword(e.target.value)} 
                      className="w-full bg-black border border-zinc-800 p-5 text-white outline-none focus:border-[#c084fc] transition-all text-sm" 
                    />
                  </div>
                  <div className="space-y-3">
                    <label className="text-[9px] font-black text-zinc-500 uppercase tracking-[0.3em] flex items-center gap-2">
                      <ShieldCheck className="w-3.5 h-3.5" /> {t.confirmPass}
                    </label>
                    <input 
                      type="password" 
                      value={confirmPassword} 
                      onChange={e => setConfirmPassword(e.target.value)} 
                      className="w-full bg-black border border-zinc-800 p-5 text-white outline-none focus:border-[#c084fc] transition-all text-sm" 
                    />
                  </div>
                </>
              )}

              <button 
                type="submit" 
                disabled={isSaving}
                className="bg-white hover:bg-[#c084fc] text-black hover:text-white px-10 py-5 text-[10px] font-black uppercase tracking-widest transition-all shadow-xl flex items-center gap-3 active:scale-95 disabled:opacity-50"
              >
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {t.saveBtn}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
